/**
 * WYSIWYG Listener Module
 * Registers event listeners for WYSIWYG editor.
 */

import { WysiwygForm } from "./wysiwyg-form";

interface WysiwygHandlerLike {
    handleTextChange(): void;
    handleInitialRender(): void;
    handlePasteCapture(event: ClipboardEvent): void;
    handleDropCapture(event: DragEvent): void;
    handleContentImageListClick(event: MouseEvent): void;
    handleFormSubmit(): void;
    handleToolbarImage(): void;
    handleToolbarVideo(): void;
}

interface WysiwygMentionHandlerLike {
    handleInputLike(event?: Event): void;
    handleKeyDown(event: KeyboardEvent): void;
    handleDropdownMouseDown(event: MouseEvent): void;
    handleDocumentMouseDown(event: MouseEvent): void;
}

interface WysiwygImageToolbarHandlerLike {
    handleEditorClick(event: MouseEvent): void;
    handleDocumentMouseDown(event: MouseEvent): void;
    handlePopoverClick(event: MouseEvent): void;
    handlePopoverInput(event: Event): void;
    handlePopoverChange(event: Event): void;
}

interface ToolbarLike {
    addHandler?(name: string, handler: () => void): void;
}

interface QuillLike {
    root: HTMLElement;
    on(eventName: string, handler: () => void): void;
    getModule(name: string): ToolbarLike | null | undefined;
}

/**
 * WysiwygListener class
 * Manages event listener registration.
 */
class WysiwygListener {
    public static bindLocalizedPlaceholder(editorDiv: HTMLElement, applyLocalizedPlaceholder: () => void): void {
        if (editorDiv.dataset.wysiwygPlaceholderBound === "1") return;

        editorDiv.dataset.wysiwygPlaceholderBound = "1";
        document.addEventListener("languageChanged", applyLocalizedPlaceholder);
    }

    public static bindTransientFilePickerChange(input: HTMLInputElement, onChange: () => void): void {
        input.addEventListener("change", onChange, { once: true });
    }

    public static bindMentionInteractions(
        quillRoot: HTMLElement,
        dropdownEl: HTMLElement,
        handler: WysiwygMentionHandlerLike,
    ): void {
        quillRoot.addEventListener("keydown", (event: Event) => {
            if (event instanceof KeyboardEvent) {
                handler.handleKeyDown(event);
            }
        });
        quillRoot.addEventListener("keyup", (event: Event) => handler.handleInputLike(event));
        quillRoot.addEventListener("click", (event: Event) => handler.handleInputLike(event));
        quillRoot.addEventListener("input", (event: Event) => handler.handleInputLike(event));
        dropdownEl.addEventListener("mousedown", (event: Event) => {
            if (event instanceof MouseEvent) {
                handler.handleDropdownMouseDown(event);
            }
        });
        document.addEventListener(
            "mousedown",
            (event: Event) => {
                if (event instanceof MouseEvent) {
                    handler.handleDocumentMouseDown(event);
                }
            },
            true,
        );
    }

    public static bindImageToolbarInteractions(
        quillRoot: HTMLElement,
        popover: HTMLElement,
        handler: WysiwygImageToolbarHandlerLike,
    ): void {
        quillRoot.addEventListener("click", (event: Event) => {
            if (event instanceof MouseEvent) {
                handler.handleEditorClick(event);
            }
        });

        document.addEventListener(
            "mousedown",
            (event: Event) => {
                if (event instanceof MouseEvent) {
                    handler.handleDocumentMouseDown(event);
                }
            },
            true,
        );

        popover.addEventListener("click", (event: Event) => {
            if (event instanceof MouseEvent) {
                handler.handlePopoverClick(event);
            }
        });

        popover.addEventListener("input", (event: Event) => handler.handlePopoverInput(event));
        popover.addEventListener("change", (event: Event) => handler.handlePopoverChange(event));
    }

    public static setup(quill: QuillLike, handler: WysiwygHandlerLike, textarea: HTMLTextAreaElement): void {
        quill.on("text-change", () => {
            handler.handleTextChange();
        });

        setTimeout(() => handler.handleInitialRender(), 0);

        quill.root.addEventListener(
            "paste",
            (event: Event) => {
                if (event instanceof ClipboardEvent) {
                    handler.handlePasteCapture(event);
                }
            },
            true,
        );

        quill.root.addEventListener("drop", (event: Event) => {
            if (event instanceof DragEvent) {
                handler.handleDropCapture(event);
            }
        });

        const contentImagesListCandidate = WysiwygForm.getFormContentImagesList(textarea);
        const contentImagesList = contentImagesListCandidate instanceof HTMLElement ? contentImagesListCandidate : null;
        if (contentImagesList) {
            contentImagesList.addEventListener("click", (event: Event) => {
                if (event instanceof MouseEvent) {
                    handler.handleContentImageListClick(event);
                }
            });
        }

        if (textarea.form) {
            textarea.form.addEventListener("submit", () => handler.handleFormSubmit());
        }

        const toolbar = quill.getModule("toolbar");
        if (toolbar && toolbar.addHandler) {
            toolbar.addHandler("image", () => handler.handleToolbarImage());
            toolbar.addHandler("video", () => handler.handleToolbarVideo());
        }
    }
}

export { WysiwygListener };
